"use client";

import type {
  SiteMetadataSummary,
  SiteMetadataCheckSummary,
} from "@/lib/site-analysis";
import type { SiteMetadataSummaryLockedCheck } from "@/types/analyze";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { Info, Lock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ButtonCopyToLLM } from "@/components/ButtonCopyToLLM";

const SITE_METADATA_DESCRIPTIONS: Record<string, string> = {
  title:
    "The main title of your webpage. Appears in browser tabs and search results.",
  description:
    "Brief description of your page content. Shows in search results under the title.",
  favicon: "Small icon displayed in browser tabs and bookmarks.",
  viewport:
    "Controls how your page displays on mobile devices and different screen sizes.",
  robotsTxt:
    "File that tells search engines which pages they can crawl and index.",
  sitemapUrl:
    "XML file that lists all pages on your website for search engines.",
  llmsTxt:
    "Machine-consumable policy file describing how LLMs may use your site.",
  headings:
    "HTML heading structure (H1, H2, H3) that organizes your content hierarchy.",
  structuredData:
    "Code that helps search engines understand your content better.",
  keywords:
    "Meta keywords tag (less important for SEO but still used by some search engines).",
  canonicalUrl:
    "Tells search engines which version of a page is the main one to avoid duplicate content.",
  language: "Specifies the language of your webpage content.",
  charset:
    "Character encoding that tells browsers how to display text characters.",
  ogTitle:
    "Open Graph title for social media sharing (Facebook, LinkedIn, etc.).",
  ogDescription: "Open Graph description for social media sharing.",
  ogImage: "Image shown when your page is shared on social media.",
  ogUrl: "Canonical URL for social media sharing.",
  ogType: "Type of content (website, article, etc.) for social media sharing.",
  twitterCard: "Twitter card type for how your content appears on Twitter.",
  twitterTitle: "Title specifically for Twitter sharing.",
  twitterDescription: "Description specifically for Twitter sharing.",
  twitterImage: "Image specifically for Twitter sharing.",
};

const SOCIAL_METADATA_IDS = [
  "ogTitle",
  "ogDescription",
  "ogImage",
  "ogUrl",
  "ogType",
  "twitterCard",
  "twitterTitle",
  "twitterDescription",
  "twitterImage",
] as const;

type SocialMetadataId = (typeof SOCIAL_METADATA_IDS)[number];

const WEBSITE_QUALITY_GOAL =
  "Update the code so all Website Quality metadata is present, accurate, and ready for production.";

const WEBSITE_QUALITY_INSTRUCTIONS = [
  "Identify the files that define Website Quality metadata (head/layout/page components).",
  "For every issue listed below, locate the existing markup; if it is missing, insert the required element in the canonical head/layout file.",
  "Apply the fix directly in the codebase with production-ready values (titles, meta tags, structured data, etc.).",
  "After editing, print the affected file paths and a short summary or diff of each change.",
  "If an item cannot be fixed automatically, add an entry to `unresolved` describing what is blocked and why.",
] as const;

interface SiteMetadataOverviewProps {
  siteMetadata?: SiteMetadataSummary | null;
  defaultExpanded?: boolean;
  hasPaidAccess?: boolean;
  onRequireUpgrade?: () => void;
  showCopyButton?: boolean;
  llmsPrompt?: string | null;
}

type MetadataCheck =
  | SiteMetadataCheckSummary
  | (SiteMetadataSummaryLockedCheck & {
      normalized?: { achieved: number; max: number };
      value?: unknown;
      note?: string;
    });

const isLockedCheck = (
  check: MetadataCheck
): check is SiteMetadataSummaryLockedCheck =>
  Boolean((check as SiteMetadataSummaryLockedCheck).locked);

function isValidImageUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Nur HTTPS erlauben für Sicherheit
    if (parsedUrl.protocol !== "https:") return false;

    // Prüfe ob es eine gültige Bild-URL ist
    const validImageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".svg",
      ".ico",
    ];
    const hasImageExtension = validImageExtensions.some((ext) =>
      parsedUrl.pathname.toLowerCase().includes(ext)
    );

    // URLs mit gültiger Bild-Extension ODER ohne Extension (für dynamische URLs) erlauben
    return hasImageExtension || parsedUrl.pathname.length > 1;
  } catch {
    return false;
  }
}

function isImageCheck(checkId: string): boolean {
  return ["favicon", "ogImage", "twitterImage"].includes(checkId);
}

function isSocialMetadataId(id: string): id is SocialMetadataId {
  return (SOCIAL_METADATA_IDS as readonly string[]).includes(id);
}

function getSiteMetadataDescription(id: string): string | null {
  return SITE_METADATA_DESCRIPTIONS[id] ?? null;
}

function getSiteMetadataStatus(
  achieved: number,
  max: number
): "passed" | "warning" | "failed" {
  if (max <= 0) return "passed";
  const ratio = achieved / max;
  if (ratio >= 0.8) return "passed";
  if (ratio >= 0.6) return "warning";
  return "failed";
}

function getObservedValue(check: SiteMetadataCheckSummary): string {
  if (typeof check.value === "string" && check.value.trim().length > 0) {
    return check.value.trim();
  }

  if (
    (Array.isArray(check.value) || typeof check.value === "object") &&
    check.value !== null
  ) {
    const serialized = JSON.stringify(check.value);
    if (serialized && serialized !== "{}") {
      return serialized;
    }
  }

  return "Missing";
}

function buildWebsiteQualityCopyPayload(
  siteMetadata?: SiteMetadataSummary | null
): string {
  const checks = (siteMetadata?.checks ?? []) as MetadataCheck[];
  const score = siteMetadata?.score ?? 0;

  const payload = {
    instructions: Array.from(WEBSITE_QUALITY_INSTRUCTIONS),
    section: "Website Quality",
    score,
    goal: WEBSITE_QUALITY_GOAL,
    issues: [] as Array<{
      id: string;
      title: string;
      status: "failed" | "warning";
      observed: string;
      note?: string;
      description: string;
    }>,
    unresolved: [] as string[],
    notes: [] as string[],
  };

  if (!checks.length) {
    payload.notes.push("Website Quality data was not captured for this crawl.");
    return JSON.stringify(payload, null, 2);
  }

  const unlockedChecks = checks.filter(
    (check): check is SiteMetadataCheckSummary => !isLockedCheck(check)
  );

  if (!unlockedChecks.length) {
    payload.notes.push("All Website Quality checks are locked for this plan.");
    return JSON.stringify(payload, null, 2);
  }

  const needsWork = unlockedChecks.filter((check) => {
    const { normalized } = check;
    return normalized.max > 0 && normalized.achieved < normalized.max;
  });

  if (!needsWork.length) {
    payload.notes.push("All Website Quality checks are currently passing.");
    return JSON.stringify(payload, null, 2);
  }

  needsWork.forEach((check) => {
    const status = getSiteMetadataStatus(
      check.normalized.achieved,
      check.normalized.max
    );
    if (status === "passed") {
      return;
    }

    const description =
      getSiteMetadataDescription(check.id) ?? "Provide the missing metadata.";
    const note =
      typeof check.note === "string" && check.note.trim().length > 0
        ? check.note.trim()
        : undefined;

    payload.issues.push({
      id: check.id,
      title: check.title,
      status,
      observed: getObservedValue(check),
      note,
      description,
    });
  });

  if (!payload.notes.length) {
    delete (payload as { notes?: string[] }).notes;
  }
  if (!payload.unresolved.length) {
    delete (payload as { unresolved?: string[] }).unresolved;
  }

  return JSON.stringify(payload, null, 2);
}

export default function SiteMetadataOverview({
  siteMetadata,
  defaultExpanded = true,
  hasPaidAccess = true,
  onRequireUpgrade,
  showCopyButton = false,
  llmsPrompt,
}: SiteMetadataOverviewProps) {
  // Use the already calculated score from backend
  const overallScore = siteMetadata?.score ?? 0;

  // Use the already calculated checks from backend
  const siteMetadataChecks = (siteMetadata?.checks ?? []) as MetadataCheck[];
  const llmsPromptText = (llmsPrompt ?? "").trim();
  const isLlmsPromptAvailable = llmsPromptText.length > 0;

  const unlockedChecks = siteMetadataChecks.filter(
    (check): check is SiteMetadataCheckSummary => !isLockedCheck(check)
  );

  // Count items by status
  const passedItemsCount = unlockedChecks.filter((check) => {
    const successRatio =
      check.normalized.max > 0
        ? check.normalized.achieved / check.normalized.max
        : 0;
    return successRatio >= 0.8;
  }).length;

  const warningItemsCount = unlockedChecks.filter((check) => {
    const successRatio =
      check.normalized.max > 0
        ? check.normalized.achieved / check.normalized.max
        : 0;
    return successRatio >= 0.6 && successRatio < 0.8;
  }).length;

  const failedItemsCount = unlockedChecks.filter((check) => {
    const successRatio =
      check.normalized.max > 0
        ? check.normalized.achieved / check.normalized.max
        : 0;
    return successRatio < 0.6;
  }).length;

  const getScoreColor = (score: number) => {
    if (score >= 80) return "var(--status-green)";
    if (score >= 60) return "var(--status-orange)";
    return "var(--status-red)";
  };

  const renderLockedCheck = (check: SiteMetadataSummaryLockedCheck) => {
    const description = getSiteMetadataDescription(check.id);

    return (
      <div
        key={`locked-${check.id}`}
        className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
      >
        <span className="mt-1 inline-block w-2 h-2 rounded-full flex-none bg-muted-foreground/40" />
        <div className="min-w-0 flex-1 text-left flex flex-col h-full">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-foreground">
                {check.title}
              </div>
              {description && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent className="w-80 max-w-[90vw]">
                    <p className="text-sm">{description}</p>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
              <Lock className="h-3 w-3" />
              Locked
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Upgrade to unlock full details for this check.
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="hidden sm:flex justify-end pr-1 text-[11px] text-muted-foreground mt-20 mb-2">
        <div className="inline-flex items-center gap-1 animate-bounce">
          <span>👇</span>
          <span>Click to open / close</span>
        </div>
      </div>

      {/* Mobile version with proper spacing */}
      <div className="sm:hidden flex justify-end pr-1 text-[11px] text-muted-foreground mt-24 mb-2">
        <div className="inline-flex items-center gap-1 animate-bounce">
          <span>👇</span>
          <span>Click to open / close</span>
        </div>
      </div>

      <CollapsibleSection
        title="Website Quality"
        subtitle={overallScore.toString()}
        subtitleColor={getScoreColor(overallScore)}
        rightContent={
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-status-green-bg text-status-green-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green" />
              {passedItemsCount}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-status-orange-bg text-status-orange-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-orange" />
              {warningItemsCount}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-status-red-bg text-status-red-foreground px-2 py-0.5 text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-status-red" />
              {failedItemsCount}
            </span>
          </div>
        }
        actionContent={
          showCopyButton ? (
            <ButtonCopyToLLM
              getCopyText={() => buildWebsiteQualityCopyPayload(siteMetadata)}
              onBeforeCopy={() => {
                if (hasPaidAccess) return true;
                onRequireUpgrade?.();
                return false;
              }}
            />
          ) : undefined
        }
        defaultExpanded={defaultExpanded}
      >
        {/* Checks organized by categories */}
        <div className="px-3 pb-4 space-y-6">
          {/* All Other Metadata */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-3">
              Site Quality
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {siteMetadataChecks
                .filter((check) => !isSocialMetadataId(check.id))
                .sort((a, b) => {
                  // Custom order for Site Metadata
                  const order = [
                    "title",
                    "description",
                    "favicon",
                    "viewport",
                    "robotsTxt",
                    "sitemapUrl",
                    "llmsTxt",
                    "headings",
                    "structuredData",
                    "keywords",
                    "canonicalUrl",
                    "language",
                    "charset",
                  ];
                  const aIndex = order.indexOf(a.id);
                  const bIndex = order.indexOf(b.id);
                  if (aIndex === -1 && bIndex === -1) return 0;
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                })
                .map((check) => {
                  if (isLockedCheck(check)) {
                    return renderLockedCheck(check);
                  }
                  const description = getSiteMetadataDescription(check.id);
                  const note =
                    typeof check.note === "string" &&
                    check.note.trim().length > 0
                      ? check.note
                      : null;
                  const showLlmsPromptButton = check.id === "llmsTxt";
                  const isLlmsPresent =
                    showLlmsPromptButton &&
                    typeof check.value === "string" &&
                    check.value.toLowerCase() === "present";
                  const statusColor = showLlmsPromptButton
                    ? isLlmsPresent
                      ? "bg-status-green"
                      : "bg-status-red"
                    : getStatusColorFromNormalized(
                        check.normalized.achieved,
                        check.normalized.max
                      );

                  return (
                    <div
                      key={check.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
                    >
                      <span
                        className={`mt-1 inline-block w-2 h-2 rounded-full flex-none ${statusColor}`}
                      />
                      <div className="min-w-0 flex-1 text-left flex flex-col h-full">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-foreground">
                              {check.title}
                            </div>
                            {description && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                                </PopoverTrigger>
                                <PopoverContent className="w-80 max-w-[90vw]">
                                  <div className="space-y-2 text-left">
                                    <p className="text-sm">{description}</p>
                                    {note && (
                                      <p className="text-xs text-muted-foreground">
                                        {note}
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                            {check.normalized.achieved}/{check.normalized.max}
                          </span>
                        </div>
                        {check.value != null && (
                          <div className="text-xs text-foreground/80 mb-3 break-words text-left leading-relaxed flex-1">
                            {check.id === "headings" &&
                            Array.isArray(check.value) ? (
                              <div className="space-y-1">
                                {check.value.map(
                                  (
                                    heading: {
                                      level: number;
                                      text: string;
                                      order: number;
                                    },
                                    index: number
                                  ) => (
                                    <div
                                      key={index}
                                      className={`flex items-center gap-2 ${
                                        heading.level === 1
                                          ? "ml-0"
                                          : heading.level === 2
                                          ? "ml-3"
                                          : heading.level === 3
                                          ? "ml-6"
                                          : heading.level === 4
                                          ? "ml-9"
                                          : heading.level === 5
                                          ? "ml-12"
                                          : "ml-15"
                                      }`}
                                    >
                                      <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-primary/10 text-primary">
                                        H{heading.level}
                                      </span>
                                      <span className="text-xs text-foreground/80">
                                        {heading.text}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : isImageCheck(check.id) &&
                              typeof check.value === "string" &&
                              isValidImageUrl(check.value) ? (
                              <div className="space-y-3">
                                <div className="flex justify-start">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={check.value}
                                    alt={`${check.title} preview`}
                                    className={`object-contain ${
                                      check.id === "ogImage"
                                        ? "w-full h-auto max-h-64"
                                        : check.id === "favicon"
                                        ? "w-12 h-12"
                                        : check.id === "twitterImage"
                                        ? "w-full h-auto max-h-64"
                                        : "w-32 h-32"
                                    }`}
                                    loading="lazy"
                                    onError={(e) => {
                                      const target =
                                        e.target as HTMLImageElement;
                                      target.style.display = "none";
                                      const parent = target.parentElement;
                                      if (parent) {
                                        parent.innerHTML =
                                          '<div class="flex items-center justify-center text-xs text-muted-foreground">Failed to load</div>';
                                      }
                                    }}
                                  />
                                </div>
                                <div className="text-xs text-foreground/60 text-left break-words">
                                  {check.value}
                                </div>
                              </div>
                            ) : check.id === "structuredData" &&
                              typeof check.value === "string" ? (
                              <div className="space-y-3">
                                <div className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed">
                                  {check.value
                                    .split("\n")
                                    .map((line, index) => {
                                      if (line.includes(":")) {
                                        // Header line - bold
                                        return (
                                          <div
                                            key={index}
                                            className="font-semibold text-foreground text-xs"
                                          >
                                            {line}
                                          </div>
                                        );
                                      } else if (line.trim().startsWith("•")) {
                                        // Bullet point - indented
                                        return (
                                          <div
                                            key={index}
                                            className="ml-4 text-xs text-foreground/70"
                                          >
                                            {line}
                                          </div>
                                        );
                                      } else if (line.trim()) {
                                        // Regular line - indented
                                        return (
                                          <div
                                            key={index}
                                            className="ml-4 text-xs text-foreground/70"
                                          >
                                            {line}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                </div>
                              </div>
                            ) : typeof check.value === "string" ? (
                              check.id === "robotsTxt" &&
                              check.value.length > 500 ? (
                                check.value.substring(0, 500) + "..."
                              ) : (
                                check.value
                              )
                            ) : typeof check.value === "object" ? (
                              JSON.stringify(check.value)
                            ) : (
                              String(check.value)
                            )}
                          </div>
                        )}
                        {/* Info badge showing note as badge - placed at the bottom */}
                        {showLlmsPromptButton && (
                          <div className="mt-3 mb-3">
                            <ButtonCopyToLLM
                              label="Copy prompt"
                              tooltipLabel="Copy this prompt to generate llms.txt"
                              className="text-[11px]"
                              aria-label="Copy prompt to generate llms.txt"
                              title="Copy prompt to generate llms.txt"
                              disabled={
                                Boolean(hasPaidAccess) && !isLlmsPromptAvailable
                              }
                              getCopyText={() => llmsPromptText}
                              onBeforeCopy={() => {
                                if (hasPaidAccess) return true;
                                onRequireUpgrade?.();
                                return false;
                              }}
                            />
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground text-left bg-muted/30 px-3 py-2 rounded-md border border-border/40 mt-auto">
                          {check.note ? (
                            String(check.note)
                          ) : (
                            <>
                              missing (add llms.txt to describe LLM usage
                              policies)
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Open Graph */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-3">
              Open Graph
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {siteMetadataChecks
                .filter((check) =>
                  [
                    "ogTitle",
                    "ogDescription",
                    "ogImage",
                    "ogUrl",
                    "ogType",
                  ].includes(check.id)
                )
                .sort((a, b) => {
                  // Custom order for Open Graph
                  const order = [
                    "ogTitle",
                    "ogDescription",
                    "ogImage",
                    "ogUrl",
                    "ogType",
                  ];
                  const aIndex = order.indexOf(a.id);
                  const bIndex = order.indexOf(b.id);
                  return aIndex - bIndex;
                })
                .map((check) => {
                  const description = getSiteMetadataDescription(check.id);
                  const note =
                    typeof check.note === "string" &&
                    check.note.trim().length > 0
                      ? check.note
                      : null;

                  if (isLockedCheck(check)) {
                    return renderLockedCheck(check);
                  }
                  const statusColor = getStatusColorFromNormalized(
                    check.normalized.achieved,
                    check.normalized.max
                  );

                  return (
                    <div
                      key={check.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
                    >
                      <span
                        className={`mt-1 inline-block w-2 h-2 rounded-full flex-none ${statusColor}`}
                      />
                      <div className="min-w-0 flex-1 text-left flex flex-col h-full">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-foreground">
                              {check.title}
                            </div>
                            {description && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                                </PopoverTrigger>
                                <PopoverContent className="w-80 max-w-[90vw]">
                                  <div className="space-y-2 text-left">
                                    <p className="text-sm">{description}</p>
                                    {note && (
                                      <p className="text-xs text-muted-foreground">
                                        {note}
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                            {check.normalized.achieved}/{check.normalized.max}
                          </span>
                        </div>
                        {check.value != null && (
                          <div className="text-xs text-foreground/80 mb-3 break-words text-left leading-relaxed flex-1">
                            {check.id === "headings" &&
                            Array.isArray(check.value) ? (
                              <div className="space-y-1">
                                {check.value.map(
                                  (
                                    heading: {
                                      level: number;
                                      text: string;
                                      order: number;
                                    },
                                    index: number
                                  ) => (
                                    <div
                                      key={index}
                                      className={`flex items-center gap-2 ${
                                        heading.level === 1
                                          ? "ml-0"
                                          : heading.level === 2
                                          ? "ml-3"
                                          : heading.level === 3
                                          ? "ml-6"
                                          : heading.level === 4
                                          ? "ml-9"
                                          : heading.level === 5
                                          ? "ml-12"
                                          : "ml-15"
                                      }`}
                                    >
                                      <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-primary/10 text-primary">
                                        H{heading.level}
                                      </span>
                                      <span className="text-xs text-foreground/80">
                                        {heading.text}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : isImageCheck(check.id) &&
                              typeof check.value === "string" &&
                              isValidImageUrl(check.value) ? (
                              <div className="space-y-3">
                                <div className="flex justify-start">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={check.value}
                                    alt={`${check.title} preview`}
                                    className={`object-contain ${
                                      check.id === "ogImage"
                                        ? "w-full h-auto max-h-64"
                                        : check.id === "favicon"
                                        ? "w-12 h-12"
                                        : check.id === "twitterImage"
                                        ? "w-full h-auto max-h-64"
                                        : "w-32 h-32"
                                    }`}
                                    loading="lazy"
                                    onError={(e) => {
                                      const target =
                                        e.target as HTMLImageElement;
                                      target.style.display = "none";
                                      const parent = target.parentElement;
                                      if (parent) {
                                        parent.innerHTML =
                                          '<div class="flex items-center justify-center text-xs text-muted-foreground">Failed to load</div>';
                                      }
                                    }}
                                  />
                                </div>
                                <div className="text-xs text-foreground/60 text-left break-words">
                                  {check.value}
                                </div>
                              </div>
                            ) : typeof check.value === "string" ? (
                              check.id === "robotsTxt" &&
                              check.value.length > 500 ? (
                                check.value.substring(0, 500) + "..."
                              ) : (
                                check.value
                              )
                            ) : typeof check.value === "object" ? (
                              JSON.stringify(check.value)
                            ) : (
                              String(check.value)
                            )}
                          </div>
                        )}
                        {/* Info badge showing note as badge - placed at the bottom */}
                        {check.note && (
                          <div className="text-xs text-muted-foreground text-left bg-muted/30 px-3 py-2 rounded-md border border-border/40 mt-auto">
                            {String(check.note)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Twitter Cards */}
          <div>
            <h3 className="text-base font-bold text-foreground mb-3">
              Twitter Cards
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {siteMetadataChecks
                .filter((check) =>
                  [
                    "twitterCard",
                    "twitterTitle",
                    "twitterDescription",
                    "twitterImage",
                  ].includes(check.id)
                )
                .sort((a, b) => {
                  // Custom order for Twitter Cards
                  const order = [
                    "twitterCard",
                    "twitterTitle",
                    "twitterDescription",
                    "twitterImage",
                  ];
                  const aIndex = order.indexOf(a.id);
                  const bIndex = order.indexOf(b.id);
                  return aIndex - bIndex;
                })
                .map((check) => {
                  const description = getSiteMetadataDescription(check.id);
                  const note =
                    typeof check.note === "string" &&
                    check.note.trim().length > 0
                      ? check.note
                      : null;

                  if (isLockedCheck(check)) {
                    return renderLockedCheck(check);
                  }
                  const statusColor = getStatusColorFromNormalized(
                    check.normalized.achieved,
                    check.normalized.max
                  );

                  return (
                    <div
                      key={check.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
                    >
                      <span
                        className={`mt-1 inline-block w-2 h-2 rounded-full flex-none ${statusColor}`}
                      />
                      <div className="min-w-0 flex-1 text-left flex flex-col h-full">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-foreground">
                              {check.title}
                            </div>
                            {description && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                                </PopoverTrigger>
                                <PopoverContent className="w-80 max-w-[90vw]">
                                  <div className="space-y-2 text-left">
                                    <p className="text-sm">{description}</p>
                                    {note && (
                                      <p className="text-xs text-muted-foreground">
                                        {note}
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                            {check.normalized.achieved}/{check.normalized.max}
                          </span>
                        </div>
                        {check.value != null && (
                          <div className="text-xs text-foreground/80 mb-3 break-words text-left leading-relaxed flex-1">
                            {isImageCheck(check.id) &&
                            typeof check.value === "string" &&
                            isValidImageUrl(check.value) ? (
                              <div className="space-y-3">
                                <div className="flex justify-start">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={check.value}
                                    alt={`${check.title} preview`}
                                    className={`object-contain ${
                                      check.id === "ogImage"
                                        ? "w-full h-auto max-h-64"
                                        : check.id === "favicon"
                                        ? "w-12 h-12"
                                        : check.id === "twitterImage"
                                        ? "w-full h-auto max-h-64"
                                        : "w-32 h-32"
                                    }`}
                                    loading="lazy"
                                    onError={(e) => {
                                      const target =
                                        e.target as HTMLImageElement;
                                      target.style.display = "none";
                                      const parent = target.parentElement;
                                      if (parent) {
                                        parent.innerHTML =
                                          '<div class="flex items-center justify-center text-xs text-muted-foreground">Failed to load</div>';
                                      }
                                    }}
                                  />
                                </div>
                                <div className="text-xs text-foreground/60 text-left break-words">
                                  {check.value}
                                </div>
                              </div>
                            ) : typeof check.value === "string" ? (
                              check.id === "robotsTxt" &&
                              check.value.length > 500 ? (
                                check.value.substring(0, 500) + "..."
                              ) : (
                                check.value
                              )
                            ) : typeof check.value === "object" ? (
                              JSON.stringify(check.value)
                            ) : (
                              String(check.value)
                            )}
                          </div>
                        )}
                        {/* Info badge showing note as badge - placed at the bottom */}
                        {check.note && (
                          <div className="text-xs text-muted-foreground text-left bg-muted/30 px-3 py-2 rounded-md border border-border/40 mt-auto">
                            {String(check.note)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </>
  );
}

function getStatusColorFromNormalized(achieved: number, max: number): string {
  if (max <= 0) return "bg-muted-foreground/40";
  const successRatio = achieved / max;
  if (successRatio >= 0.8) return "bg-status-green";
  if (successRatio >= 0.6) return "bg-status-orange";
  return "bg-status-red";
}
